import React from "react";
import { Phone, Smartphone, Mail, MapPin, Globe, Clock, Instagram } from "lucide-react";
import { COMPANY } from "../data/site";
import { WhatsAppIcon } from "../components/icons";
import { ConsultationForm, ContactForm, ApplicationForm } from "../components/Forms";

const InfoRow = ({ icon: Icon, label, value, href }) => {
  const content = (
    <>
      <div className="w-11 h-11 rounded-xl bg-brand-lightBlue flex items-center justify-center shrink-0">
        <Icon className="w-6 h-6 text-brand-darkBlue" />
      </div>
      <div>
        <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">{label}</p>
        <p className="text-lg text-brand-darkBlue font-medium">{value}</p>
      </div>
    </>
  );
  if (href) {
    return (
      <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="flex items-start gap-4 group">
        {content}
      </a>
    );
  }
  return <div className="flex items-start gap-4">{content}</div>;
};

export default function Contact() {
  return (
    <div data-testid="contact-page">
      <section className="bg-brand-darkBlue py-16 md:py-20 px-5 md:px-10">
        <div className="max-w-7xl mx-auto">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-brand-red mb-3">Contact Us</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight">We're here to help</h1>
          <p className="mt-5 text-lg text-white/80 max-w-2xl">Reach out to book a consultation, ask a question, or join our care team.</p>
        </div>
      </section>

      {/* Info + Hours */}
      <section className="py-20 px-5 md:px-10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-8">
            <InfoRow icon={Phone} label="Telephone" value={COMPANY.phone} href={`tel:${COMPANY.phone}`} />
            <InfoRow icon={Smartphone} label="Mobile" value={COMPANY.mobile} href={`tel:${COMPANY.mobile}`} />
            <InfoRow icon={WhatsAppIcon} label="WhatsApp" value="Chat with us" href={`https://wa.me/${COMPANY.whatsapp}`} />
            <InfoRow icon={Mail} label="Email" value={COMPANY.email} href={`mailto:${COMPANY.email}`} />
            <InfoRow icon={Instagram} label="Instagram" value={`@${COMPANY.instagram}`} href={COMPANY.instagramUrl} />
            <InfoRow icon={Globe} label="Website" value={COMPANY.website} />
            <InfoRow icon={MapPin} label="Address" value={`${COMPANY.address.city}, ${COMPANY.address.state}`} />
          </div>
          <div className="bg-brand-lightBlue rounded-2xl p-8 border border-blue-200">
            <div className="flex items-center gap-3 mb-5">
              <Clock className="w-7 h-7 text-brand-red" />
              <h2 className="text-xl font-semibold text-brand-darkBlue">Hours of Operation</h2>
            </div>
            <ul className="space-y-3">
              {COMPANY.hours.map((h) => (
                <li key={h.day} className="flex justify-between text-lg text-brand-darkBlue">
                  <span className="font-medium">{h.day}</span>
                  <span className="text-gray-600">{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Follow Us — Instagram QR */}
      <section className="pb-4 px-5 md:px-10" data-testid="instagram-section">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl border border-border shadow-md p-8 flex flex-col sm:flex-row items-center gap-8">
            <img
              src={COMPANY.instagramQR}
              alt={`Instagram QR code for @${COMPANY.instagram}`}
              className="w-40 h-40 rounded-xl object-contain shrink-0"
              data-testid="instagram-qr"
            />
            <div className="text-center sm:text-left">
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-brand-red mb-2">Follow Us</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-brand-darkBlue">Connect on Instagram</h2>
              <p className="mt-3 text-lg text-gray-800">Scan the code or tap below to see our latest updates and stories.</p>
              <a
                href={COMPANY.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-follow-btn"
                className="mt-5 inline-flex items-center gap-2 bg-brand-darkBlue text-white font-semibold py-3 px-7 rounded-full hover:bg-brand-blue transition-colors"
              >
                <Instagram className="w-5 h-5" /> @{COMPANY.instagram}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation Form */}
      <section id="consultation" className="scroll-mt-24 bg-brand-lightBlue py-20 px-5 md:px-10">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-brand-red mb-3">Book Consultation</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-brand-darkBlue mb-8">Request a consultation</h2>
          <div className="bg-white rounded-2xl shadow-md border border-border p-8 md:p-10">
            <ConsultationForm />
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="message" className="scroll-mt-24 py-20 px-5 md:px-10">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-brand-red mb-3">Send a Message</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-brand-darkBlue mb-8">Get in touch</h2>
          <div className="bg-white rounded-2xl shadow-md border border-border p-8 md:p-10">
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="apply" className="scroll-mt-24 bg-brand-darkBlue py-20 px-5 md:px-10">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-brand-red mb-3">Careers</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-2">Interested in becoming a part of the HHS team?</h2>
          <p className="text-lg text-white/80 mb-8">Apply here — we'd love to hear from you.</p>
          <div className="bg-white rounded-2xl shadow-md border border-border p-8 md:p-10">
            <ApplicationForm />
          </div>
        </div>
      </section>
    </div>
  );
}
